import { z } from 'zod/v4';

export const AssessmentBlueprintRiskSchema = z.object({
  id: z.string(),
  title: z.string(),
});

/**
 * `GET /risk-analysis/assessment-blueprints/:id` response, ported from the legacy
 * `handlers/v3/assessment-blueprints/get/index.js` -> `data/assessment-blueprints/get.js`'s
 * `getCompleteDetails`. Also the `POST /risk-analysis/assessment-blueprints` (create)
 * response — both legacy handlers called the same `getCompleteDetails(id)`.
 *
 * Trimmed to the columns the legacy `getByIdQuery` actually selects and that a
 * caller (the pinned integration test, or either frontend) reads —
 * `assessmentCount`/`createdAt`/`updatedAt` were declared in the legacy
 * JSON-schema response but never populated by this query, so they are not carried
 * over. `createdByUserId` IS carried over: the create endpoint's pinned
 * integration test asserts it in the response body.
 */
export const AssessmentBlueprintDetailResourceSchema = z.object({
  id: z.string(),
  templateId: z.string().nullable(),
  assessmentFormulaId: z.string().nullable(),
  assessmentFormulaTitle: z.string().nullable(),
  assessmentFormulaDescription: z.string().nullable(),
  assessmentFormulaTags: z.array(z.string()).nullable(),
  assessmentFormulaVisibility: z.string().nullable(),
  assessmentFormulaCreatedByCompanyId: z.string().nullable(),
  scoreFormula: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  name: z.string(),
  goal: z.string().nullable(),
  isArchived: z.boolean(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string(),
  risks: z.array(AssessmentBlueprintRiskSchema),
  assessmentCompanyIds: z.array(z.string()),
});

export type AssessmentBlueprintDetailResource = z.infer<
  typeof AssessmentBlueprintDetailResourceSchema
>;

export const GetAssessmentBlueprintParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export const ASSESSMENT_BLUEPRINTS_LIST_DEFAULT_PAGE_SIZE = 15;
export const ASSESSMENT_BLUEPRINTS_LIST_MAX_PAGE_SIZE = 500;
export const ASSESSMENT_BLUEPRINTS_LIST_FILTER_MAX_LENGTH = 512;

/**
 * Keys of `sortingColumnsMapping` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`.
 * `type` and `isArchived` are in that file's `FILTER_COLUMNS_ENUM` but never in
 * `SORT_COLUMNS_ENUM` — they are not sortable, only filterable.
 */
export const AssessmentBlueprintsListSortColumns = [
  'name',
  'goal',
  'updatedAt',
  'assessmentFormulaTitle',
  'assessmentCount',
] as const;

const sortColumnAlternatives = AssessmentBlueprintsListSortColumns.join('|');
const sortParamSchema = z
  .string()
  .regex(
    new RegExp(
      `^((${sortColumnAlternatives}):(ASC|DESC|asc|desc))(,((${sortColumnAlternatives}):(ASC|DESC|asc|desc)))*$`,
    ),
  )
  .optional();

/**
 * `GET /risk-analysis/assessment-blueprints` querystring, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`'s
 * `list.querystring` (`...paginationParams, filter, sort`).
 */
export const AssessmentBlueprintsListQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(ASSESSMENT_BLUEPRINTS_LIST_MAX_PAGE_SIZE)
    .default(ASSESSMENT_BLUEPRINTS_LIST_DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  filter: z
    .string()
    .max(ASSESSMENT_BLUEPRINTS_LIST_FILTER_MAX_LENGTH)
    .optional(),
});

export type AssessmentBlueprintsListQuery = z.infer<
  typeof AssessmentBlueprintsListQuerySchema
>;

/**
 * One row of `GET /risk-analysis/assessment-blueprints`, ported from
 * `data/assessment-blueprints/queries/list.js`'s `listQuery`. Trimmed to the
 * columns that query selects and a caller (the pinned integration test, or
 * either frontend's list table / edit dialog) reads — `templateId`,
 * `assessmentFormulaDescription`, `assessmentFormulaTags`, `risks`, and
 * `assessmentCompanyIds` are all detail-only fields this query never selects.
 */
export const AssessmentBlueprintListRowSchema = z.object({
  id: z.string(),
  assessmentFormulaId: z.string().nullable(),
  assessmentFormulaTitle: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  name: z.string(),
  goal: z.string().nullable(),
  isArchived: z.boolean(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string(),
  assessmentCount: z.number(),
  updatedAt: z.string().nullable(),
  /** The last create, edit or archive, from the blueprint's history. */
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyId: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
});

export type AssessmentBlueprintListRow = z.infer<
  typeof AssessmentBlueprintListRowSchema
>;

export const ASSESSMENT_BLUEPRINTS_BY_COMPANY_DEFAULT_PAGE_SIZE = 15;
export const ASSESSMENT_BLUEPRINTS_BY_COMPANY_MAX_PAGE_SIZE = 500;
export const ASSESSMENT_BLUEPRINTS_BY_COMPANY_FILTER_MAX_LENGTH = 512;

/**
 * `GET /risk-analysis/assessment-blueprints/:assessedCompanyId/assessments`
 * querystring, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`'s
 * `listByCompanyId.querystring`.
 *
 * Two deltas from that legacy schema, both intentional:
 * - No `sort` — the legacy route handler never reads `request.query.sort` or
 *   passes `sortParams` through to `list()`, so `listByCompanyQuery` never gets
 *   an `ORDER BY`. Sorting was never actually wired for this endpoint.
 * - No `assessedCompanyId` — the legacy schema declares it as a querystring
 *   property, but the handler reads the value from `request.params` (the URL
 *   path segment) exclusively. The querystring copy is accepted-but-ignored
 *   noise; the pinned integration test locks in that a different
 *   `?assessedCompanyId=` has no effect.
 *
 * Plain `z.object` (not `z.strictObject`) so an unrecognised query param is
 * silently dropped rather than rejected — matching the legacy behaviour, where
 * `additionalProperties: false` is defeated by Fastify's default
 * `removeAdditional: true` ajv option (also pinned by the legacy test).
 */
export const AssessmentBlueprintsByCompanyListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(ASSESSMENT_BLUEPRINTS_BY_COMPANY_MAX_PAGE_SIZE)
    .default(ASSESSMENT_BLUEPRINTS_BY_COMPANY_DEFAULT_PAGE_SIZE),
  filter: z
    .string()
    .max(ASSESSMENT_BLUEPRINTS_BY_COMPANY_FILTER_MAX_LENGTH)
    .optional(),
});

export type AssessmentBlueprintsByCompanyListQuery = z.infer<
  typeof AssessmentBlueprintsByCompanyListQuerySchema
>;

/**
 * No length cap — the legacy route declares no `params` schema at all for the
 * `:assessedCompanyId` path segment, so any non-empty string is accepted.
 */
export const GetAssessmentBlueprintsByCompanyParamsSchema = z.object({
  assessedCompanyId: z.string().min(1),
});

/**
 * One row of
 * `GET /risk-analysis/assessment-blueprints/:assessedCompanyId/assessments`,
 * ported from `data/assessment-blueprints/queries/list.js`'s
 * `listByCompanyQuery` — every column that query selects, matching the pinned
 * integration test's asserted shape.
 */
export const AssessmentBlueprintByCompanyRowSchema = z.object({
  id: z.string(),
  templateId: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  name: z.string(),
  goal: z.string().nullable(),
  type: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type AssessmentBlueprintByCompanyRow = z.infer<
  typeof AssessmentBlueprintByCompanyRowSchema
>;

/**
 * `POST /risk-analysis/assessment-blueprints/archive` body, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`'s
 * `archive.body`.
 *
 * One deliberate delta: the legacy schema does NOT mark `assessmentIds` as
 * required, so a missing field slipped past validation and the handler blew up
 * downstream with an uncaught 500 (pinned by the legacy integration test as a
 * "parity" case). Zod fields are required by default, so simply not adding
 * `.optional()` here fixes that — a missing/omitted `assessmentIds` now cleanly
 * 400s instead. No caller ever depended on getting a 500 for this.
 */
export const BulkArchiveAssessmentBlueprintsBodySchema = z.strictObject({
  assessmentIds: z.array(z.string().min(1).max(36)).min(1),
});

export type BulkArchiveAssessmentBlueprintsBody = z.infer<
  typeof BulkArchiveAssessmentBlueprintsBodySchema
>;

/**
 * `POST /risk-analysis/assessment-blueprints` (create) body, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`'s
 * `create.body`.
 *
 * Trimmed to the properties the legacy `create` handler actually wires through to
 * `createBlueprint`/`createQuery`: `assessmentFormulaTitle` is declared (and
 * validated) by the legacy schema but never read by the handler, so it is dropped
 * here — an oversize value that used to 400 now passes straight through as
 * accepted-but-ignored noise, matching Fastify's own `removeAdditional` behaviour
 * for genuinely unknown properties (see the pinned "additional properties are
 * silently stripped" integration test).
 *
 * `createdByUserId`/`createdByCompanyId` ARE kept, even though they read like
 * server-derived audit columns: the legacy data layer spread the request body
 * over the ctx-derived values (`{id, type, createdByUserId, createdByCompanyId,
 * ...body}`), so a caller-supplied value silently wins. That is a real authz
 * hole, but it's the live behaviour this migration preserves rather than
 * silently fixes — see the pinned "parity: spoofable audit columns" integration
 * test.
 *
 * Plain `z.object` (not `z.strictObject`), matching the legacy schema's
 * `additionalProperties: false` being defeated by Fastify's default
 * `removeAdditional: true` ajv option — an unrecognised property is dropped, not
 * rejected with a 400.
 */
/**
 * DELTA-2516. The legacy ajv schemas were
 * `goal: {oneOf: [{type:'string',minLength:1,maxLength:512},{type:'null'}]}` and
 * the equivalent for `tags`, validated under Fastify's default `coerceTypes`.
 * ajv coerced an empty string into the `{type:'null'}` branch, so
 * `POST {goal: ''}` returned 201 with the column stored as NULL; and because
 * `coerceTypes: 'array'` unwraps a single-element array first, `{tags: ['']}`
 * landed as NULL too.
 *
 * The migration to Zod ported the *shape* faithfully but not the *coercion*, so
 * both began returning `400 body/goal Too small` — which is what DELTA-2516
 * reported, since the v1 dashboard sends `goal: ''` for an untouched optional
 * Goal field. These restore the old acceptance at the contract, so every client
 * benefits rather than only the one that reported it.
 *
 * The rule is deliberately one line — "blank means absent" — rather than a
 * bug-compatible replay of ajv's single-element-array unwrapping, which also
 * made `['', 'b']` a 400 while `['']` was a 201. For blank values it is never
 * *more* restrictive than the legacy schema, so nothing that used to work can
 * start failing; it is deliberately more permissive for `[]` and `['', 'b']`,
 * which legacy rejected only as an artifact of that unwrapping.
 *
 * One legacy behaviour is deliberately NOT restored: ajv's scalar coercion also
 * turned `tags: [1]` into `['1']`. That divergence arrived with the migration
 * and predates this change (Zod has always rejected it), nobody has reported
 * it, and silently stringifying a numeric tag is worse than a 400 — so the
 * inner `z.string()` is left to reject non-strings.
 *
 * Both run at the Fastify validation layer AND again in each handler's own
 * `.parse()`, so they must be idempotent — they are: `null` and an array of
 * non-blank strings are both fixed points.
 */
const blankStringToNull = (value: unknown): unknown =>
  typeof value === 'string' && value.trim() === '' ? null : value;

const blankTagsToNull = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value;
  // Non-strings are kept so the inner `z.string()` still rejects them.
  const kept = value.filter(
    tag => typeof tag !== 'string' || tag.trim() !== '',
  );
  return kept.length > 0 ? kept : null;
};

const GoalBodyField = z.preprocess(
  blankStringToNull,
  z.string().min(1).max(512).nullable().optional(),
);

const TagsBodyField = z.preprocess(
  blankTagsToNull,
  z.array(z.string().min(1)).min(1).nullable().optional(),
);

export const CreateAssessmentBlueprintBodySchema = z.object({
  assessmentFormulaId: z.string().min(1).max(36),
  name: z.string().min(1).max(64),
  goal: GoalBodyField,
  tags: TagsBodyField,
  createdByUserId: z.string().min(1).max(36).nullable().optional(),
  createdByCompanyId: z.string().min(1).max(36).nullable().optional(),
});

export type CreateAssessmentBlueprintBody = z.infer<
  typeof CreateAssessmentBlueprintBodySchema
>;

/**
 * `PATCH /risk-analysis/assessment-blueprints/:id` body, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`'s
 * `update.body` (which reuses the same `assessmentBluePrintProperties` as
 * `create.body`).
 *
 * Trimmed further than the create schema: `createdByUserId`/`createdByCompanyId`
 * are declared there too, but the legacy update handler's `updateQuery`
 * destructures only `{assessmentFormulaId, tags, name, goal, type}` from the
 * body — those two are accepted-but-ignored noise for update and are dropped
 * here (unlike create, where they ARE wired through and kept for that reason).
 * `assessmentFormulaTitle` is dropped for the same reason as in create.
 *
 * `goal`/`tags` map to a full-column overwrite, not a merge: the legacy UPDATE
 * sets every column unconditionally, so an omitted `goal`/`tags` clears the
 * column to `NULL` rather than leaving the existing value untouched — see
 * `AssessmentBlueprintService.update`.
 *
 * Plain `z.object` (not `z.strictObject`), matching the legacy schema's
 * `additionalProperties: false` being defeated by Fastify's default
 * `removeAdditional: true` ajv option — an unrecognised property is dropped,
 * not rejected with a 400 (see the pinned "additional properties are silently
 * stripped" integration test, ported from the legacy one).
 */
export const UpdateAssessmentBlueprintBodySchema = z.object({
  assessmentFormulaId: z.string().min(1).max(36),
  name: z.string().min(1).max(64),
  // Same legacy-parity normalisers as create — see their comment above.
  goal: GoalBodyField,
  tags: TagsBodyField,
});

export type UpdateAssessmentBlueprintBody = z.infer<
  typeof UpdateAssessmentBlueprintBodySchema
>;

/**
 * `GET /risk-analysis/assessment-blueprints/:id/company/:companyId/history`
 * params, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-blueprint.js`'s
 * `history.params`. `companyId` is the *assessed* company (the URL's second
 * path segment) — not to be confused with the caller's own `ctx.companyId`,
 * which the route reads separately to scope the query by
 * `created_by_company_id`.
 */
export const GetAssessmentBlueprintHistoryParamsSchema = z.object({
  id: z.string().min(1).max(36),
  companyId: z.string().min(1).max(36),
});

export type GetAssessmentBlueprintHistoryParams = z.infer<
  typeof GetAssessmentBlueprintHistoryParamsSchema
>;

/**
 * One entry of
 * `GET /risk-analysis/assessment-blueprints/:id/company/:companyId/history`,
 * ported from `handlers/v3/assessment-blueprints/history/index.js`'s mapped
 * shape (`{id, netRisk, status, date}`) over
 * `data/assessment-blueprints/queries/get.js`'s `historyQuery`.
 *
 * `netRisk` is nullable: `risk_analysis_service_assessments.net_score` is a
 * nullable column, genuinely unset while an assessment is `CALCULATING` or
 * after it `FAILED`. The legacy JSON-schema response declared it as a
 * non-nullable `number`, but fast-json-stringify serializes a `null` in a
 * `number`-typed field leniently — Zod's stricter response validation turns
 * the same data into a hard 500 (`FST_ERR_RESPONSE_SERIALIZATION`, seen in
 * prod). `date` is nullable for the same reason (matching the legacy
 * `type: ['string', 'null']`).
 */
export const AssessmentBlueprintHistoryEntrySchema = z.object({
  id: z.string(),
  netRisk: z.number().nullable(),
  status: z.string(),
  date: z.string().nullable(),
});

export type AssessmentBlueprintHistoryEntry = z.infer<
  typeof AssessmentBlueprintHistoryEntrySchema
>;
